import { isNativeAppWebview } from 'core/utils/webapp';
import React, { KeyboardEvent, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectCustomizableOption } from './SelectCustomizable';

let globalSelectCounter = 0;

type ActionMeta = { action: string; removedValue?: SelectCustomizableOption };

type StyleFn = (base: Record<string, unknown>, state?: Record<string, unknown>) => Record<string, unknown>;

type SharedStyles = {
  menu: StyleFn;
  menuPortal: StyleFn;
  control: StyleFn;
  option: StyleFn;
};

type SelectStyles = SharedStyles & {
  valueContainer?: StyleFn;
};

type SharedProps = {
  _listboxId: string;
  _focusedOptionId: string | null;
  _onOptionFocusChange: (id: string) => void;
  _errorId: string | undefined;
  _hasError: boolean;
  _required: boolean;
  _removeLabel: string;
  _valueId: string;
  onKeyDown: (event: KeyboardEvent) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  tabSelectsValue: boolean;
  iconTypeIndicator: string | undefined;
  showValuesAsList: boolean;
  showValuesAsText: boolean;
  useShortLabels: boolean | undefined;
};

type UseSelectCustomizableParams = {
  inputId?: string;
  name?: string;
  isSearchable?: boolean;
  error?: string;
  noOptionsMessage?: string;
  label?: ReactNode;
  ariaLabel?: string;
  placeholder?: ReactNode;
  required?: boolean;
  variant?: 'default' | 'subtle';
  size?: 'default' | 'small';
  tagSize?: 'sm' | 'md';
  iconTypeIndicator?: string;
  multiValueDisplay?: 'inline' | 'sortedList';
  showValuesAsText?: boolean;
  useShortLabels?: boolean;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
  onCreateOption?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

type UseSelectCustomizableReturn = {
  resolvedInputId: string;
  labelId: string;
  listboxId: string;
  errorId: string;
  selectRef: MutableRefObject<unknown>;
  finalIsSearchable: boolean | undefined;
  finalMenuPlacement: 'top' | 'auto';
  getNoOptionsMessage: () => string;
  getCreateLabel: (text: string) => string;
  getScreenReaderStatus: (state: { count: number }) => string;
  sharedStyles: SharedStyles;
  selectStyles: SelectStyles;
  resolvedPlaceholder: ReactNode;
  resolvedAriaLabel: string | undefined;
  labelTitle: string;
  className: string;
  isSortedList: boolean;
  sharedProps: SharedProps;
  handleCreatableChange: (newValue: unknown, actionMeta?: ActionMeta) => void;
  formatGroupLabel: (data: { label: string; options: unknown[] }) => React.ReactElement;
  getOptionLabel: (data: SelectCustomizableOption, meta: { context: string }) => ReactNode;
};

type SelectInstance = {
  openMenu?: (position: string) => void;
  // Nodo del input que react-select haya renderizado (el propio o su DummyInput).
  inputRef?: HTMLInputElement | null;
  // Props que el StateManager pasa al Select interno ya RESUELTAS (prop controlada
  // del consumidor o estado interno, lo que aplique).
  props?: { menuIsOpen?: boolean };
};

type SelectRefValue = {
  select?: SelectInstance & { select?: SelectInstance };
};

function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /ipad/.test(userAgent) || (navigator.maxTouchPoints > 1 && /macintosh/.test(userAgent));
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  return isIPad || isMobile || isNativeAppWebview();
}

function getControlBorderStyles(hasError: boolean): Record<string, unknown> {
  if (hasError) {
    return {
      border: '2px solid var(--select-customizable-border-error)',
      '&:hover': { borderColor: 'var(--select-customizable-border-error)' },
    };
  }
  return {
    border: '1px solid var(--select-customizable-border-default)',
    '&:hover': { border: '2px solid var(--select-customizable-border-hover)' },
  };
}

function getInnerSelectInstance(ref: MutableRefObject<unknown>): SelectInstance | null {
  if (!ref.current) return null;
  // ref.current = StateManager instance
  // ref.current.select = inner Select (regular) OR Creatable (creatable)
  // ref.current.select.select = inner Select (creatable only)
  const { select: inner } = ref.current as SelectRefValue;
  if (!inner) return null;
  return inner.select ?? inner;
}

// Aviso en desarrollo: el select cae al placeholder como nombre accesible
// cuando no hay `label` ni `aria-label`. Es un stopgap; lo suyo es un nombre
// específico y con sentido para el campo (ver "Deuda técnica" en la mdx).
function warnMissingAccessibleName(): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    '[SelectCustomizable] Sin `label` ni `aria-label`: el nombre accesible cae al `placeholder`. ' +
      'Pasa un `label` visible o un `aria-label` específico y con sentido para el campo.'
  );
}

function setOrRemoveAttribute(element: HTMLElement, name: string, value: string | null): void {
  if (value) {
    element.setAttribute(name, value);
    return;
  }
  element.removeAttribute(name);
}

function getLabelTitle(labelValue: ReactNode): string {
  if (typeof labelValue === 'string') return labelValue;
  if (!React.isValidElement(labelValue)) return '';

  const { children } = labelValue.props as { children?: ReactNode };
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.filter((child) => typeof child === 'string').join(' ');

  return '';
}

export default function useSelectCustomizable({
  inputId,
  name,
  isSearchable,
  error,
  noOptionsMessage,
  label,
  ariaLabel,
  placeholder,
  required,
  variant = 'default',
  size = 'default',
  tagSize = 'md',
  iconTypeIndicator,
  multiValueDisplay = 'inline',
  showValuesAsText = false,
  useShortLabels,
  onChange,
  onCreateOption,
  onKeyDown: consumerOnKeyDown,
  onMenuOpen: consumerOnMenuOpen,
  onMenuClose: consumerOnMenuClose,
}: UseSelectCustomizableParams): UseSelectCustomizableReturn {
  const { t } = useTranslation(['placeholder']);

  const instanceIdRef = useRef(`select-accessible-${++globalSelectCounter}`);
  const resolvedInputId = inputId || name || instanceIdRef.current;
  const labelId = `${resolvedInputId}-label`;
  const listboxId = `${resolvedInputId}-listbox`;
  const errorId = `${resolvedInputId}-error`;
  const valueId = `${resolvedInputId}-value`;

  const selectRef = useRef<unknown>(null);
  // El ref es lo que lee `getScreenReaderStatus`, que tiene que ser estable (TAN-10401);
  // el estado paralelo no se lee nunca: existe solo para provocar un render al abrir/cerrar
  // (la fuente de verdad del efecto es `inner.props.menuIsOpen`, ya resuelta).
  const menuIsOpenRef = useRef(false);
  const [, setIsMenuOpen] = useState(false);
  const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);

  const isOnMobileDevice = useMemo(isMobileDevice, []);
  const finalIsSearchable = isOnMobileDevice ? false : isSearchable;
  const finalMenuPlacement: 'top' | 'auto' = isOnMobileDevice ? 'top' : 'auto';

  const isSortedList = multiValueDisplay === 'sortedList';

  // Nombre accesible: si hay label visible manda el aria-labelledby; si no,
  // usa el aria-label del consumidor y, en su defecto, cae al placeholder
  // (que siempre es un string) para que el combobox nunca quede sin nombre.
  // `||` (no `??`) para que un placeholder="" también caiga al default.
  const resolvedPlaceholder = placeholder || t('Select');
  const placeholderName = typeof resolvedPlaceholder === 'string' ? resolvedPlaceholder : undefined;
  const resolvedAriaLabel = ariaLabel ?? (label ? undefined : placeholderName);
  const labelTitle = getLabelTitle(label);

  const hasAccessibleName = Boolean(label) || Boolean(ariaLabel);
  useEffect(() => {
    if (!hasAccessibleName) warnMissingAccessibleName();
  }, [hasAccessibleName]);

  // TAN-10504 — patrón combobox cuando react-select no usa nuestro `Input`.
  // Con `isSearchable={false}`, `renderInput()` ignora `components.Input` y monta su
  // `DummyInput` interno (un <input readOnly> invisible) con solo aria-autocomplete,
  // aria-label y aria-labelledby: `AccessibleInput` queda inerte y el lector anuncia un
  // campo de texto en vez de un combo contraído/expandido (WCAG 2.1 · 4.1.2). react-select
  // v3 no expone `components.DummyInput`, así que el patrón se aplica sobre el nodo DOM.
  // OJO: siete de los ocho atributos no existen en las props React de ese input, pero
  // `aria-autocomplete` SÍ (react-select se lo pasa como prop al DummyInput): el override
  // solo se sostiene porque este efecto se re-aplica tras cada render (ver el comentario
  // final sobre las dependencias). El estado `isMenuOpen` existe solo para provocar el
  // render (y por tanto esta pasada) al abrir/cerrar el menú, que es además lo que hace
  // que el id del valor se recoja justo tras la primera selección.
  useEffect(() => {
    if (finalIsSearchable !== false) return;

    const inner = getInnerSelectInstance(selectRef);
    const input = inner?.inputRef;
    // El modo creatable no reenvía `isSearchable` a react-select, así que ahí sigue
    // renderizándose un `Input` real gobernado por React: solo se decora el DummyInput.
    if (!input?.readOnly) return;

    // `inner.props.menuIsOpen` es el estado ya resuelto (prop controlada o interno). No
    // sirve `isMenuOpen`, derivado de los callbacks: con `menuIsOpen={false}` controlado,
    // react-select dispara onMenuOpen igualmente (openMenu → onMenuOpen es incondicional)
    // sin abrir nada, y el atributo se quedaría en "true" para toda la sesión de foco.
    const menuIsOpen = Boolean(inner?.props?.menuIsOpen);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', menuIsOpen ? 'true' : 'false');
    input.setAttribute('aria-controls', listboxId);
    // Este modo nunca filtra, así que el "list" que pone react-select prometería un
    // autocompletado que no existe.
    input.setAttribute('aria-autocomplete', 'none');

    setOrRemoveAttribute(input, 'aria-activedescendant', focusedOptionId);
    setOrRemoveAttribute(input, 'aria-invalid', error ? 'true' : null);
    setOrRemoveAttribute(input, 'aria-required', required ? 'true' : null);

    // El valor seleccionado se pinta en un <div> hermano que el lector se salta al enfocar,
    // así que se referencia por aria-describedby, pero solo si ese nodo ya está en el DOM:
    // un id colgante no lo anuncia nadie. Y axe tampoco lo caza — aria-valid-attr-value lo
    // deja en "incomplete", nunca en violation —, así que los tests lo asertan directamente.
    // El modo buscable resuelve lo mismo declarativamente en getDescribedBy
    // (SelectCustomizable.tsx): si cambias un lado, revisa el otro.
    const hasRenderedValue = Boolean(document.getElementById(valueId));
    const describedBy = [hasRenderedValue ? valueId : null, error ? errorId : null].filter(Boolean).join(' ');
    setOrRemoveAttribute(input, 'aria-describedby', describedBy || null);
    // Sin lista de dependencias A PROPÓSITO, y es obligatorio que siga así: (1) la presencia
    // del valor se lee del DOM, no de una dependencia, y hay que resincronizar en cada render
    // para que un valor que desaparece no deje un aria-describedby colgante; y (2) React sí
    // gestiona aria-autocomplete en el DummyInput, así que cualquier remontaje lo devuelve a
    // "list" y esta pasada tiene que correr tras cada render para re-corregirlo. Añadir un
    // array de deps "para optimizar" rompería ambas cosas en silencio (exhaustive-deps no se
    // activa cuando el array falta). Es idempotente y solo escribe atributos.
  });

  const className = `select-customizable select-customizable--${variant} select-customizable--size-${size} select-customizable--tag-${tagSize}${
    error ? ' select-customizable--error' : ''
  }`;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' && !menuIsOpenRef.current) {
      event.preventDefault();
      const instance = getInnerSelectInstance(selectRef);
      if (instance && typeof instance.openMenu === 'function') {
        instance.openMenu('first');
      }
    }
  }, []);

  // El estado `isMenuOpen` solo lo lee el efecto de decoración del modo no buscable
  // (que sale por `return` si el select es buscable): en el resto de selects, actualizarlo
  // solo costaría un re-render del componente por cada apertura/cierre.
  const handleMenuOpen = useCallback(() => {
    menuIsOpenRef.current = true;
    if (finalIsSearchable === false) setIsMenuOpen(true);
  }, [finalIsSearchable]);

  const handleMenuClose = useCallback(() => {
    menuIsOpenRef.current = false;
    if (finalIsSearchable === false) setIsMenuOpen(false);
    setFocusedOptionId(null);
  }, [finalIsSearchable]);

  const onOptionFocusChange = useCallback((id: string) => {
    setFocusedOptionId(id || null);
  }, []);

  const composedKeyDown = useCallback(
    (event: KeyboardEvent) => {
      handleKeyDown(event);
      if (consumerOnKeyDown) consumerOnKeyDown(event);
    },
    [handleKeyDown, consumerOnKeyDown]
  );

  const composedMenuOpen = useCallback(() => {
    handleMenuOpen();
    if (consumerOnMenuOpen) consumerOnMenuOpen();
  }, [handleMenuOpen, consumerOnMenuOpen]);

  const composedMenuClose = useCallback(() => {
    handleMenuClose();
    if (consumerOnMenuClose) consumerOnMenuClose();
  }, [handleMenuClose, consumerOnMenuClose]);

  // react-select v3 Creatable tiene un bug de igualdad por referencia: cuando el
  // menú se re-renderiza entre el hover y el click (frecuente al escribir), la
  // referencia de la opción clicada deja de coincidir con state.newOption, y la
  // opción nueva se cuela por onChange en vez de disparar onCreateOption. Se
  // reenvía cualquier valor __isNew__ a onCreateOption.
  const handleCreatableChange = useCallback(
    (newValue: unknown, actionMeta?: ActionMeta) => {
      const candidate = Array.isArray(newValue) ? newValue.at(-1) : (newValue as SelectCustomizableOption | null);
      if (candidate?.__isNew__ && onCreateOption) {
        onCreateOption(String(candidate.value));
        return;
      }
      if (onChange) onChange(newValue, actionMeta);
    },
    [onChange, onCreateOption]
  );

  const getNoOptionsMessage = useCallback(() => {
    return noOptionsMessage ?? t('placeholder:No options');
  }, [noOptionsMessage, t]);

  // Solo se anuncia el recuento con el menú abierto; cerrado, react-select daría
  // un "0 results available" falso. Texto en inglés (i18n fuera de alcance).
  const getScreenReaderStatus = useCallback(({ count }: { count: number }): string => {
    if (menuIsOpenRef.current) return `${count} result${count === 1 ? '' : 's'} available`;
    return '';
  }, []);

  const getCreateLabel = useCallback((text: string) => `${t('placeholder:Create new section')}: ${text}`, [t]);

  const formatGroupLabel = useCallback(
    (data: { label: string; options: unknown[] }): React.ReactElement => (
      <div className="select-customizable__group-label">
        <span>{data.label}</span>
        <span className="select-customizable__group-badge">{data.options.length}</span>
      </div>
    ),
    []
  );

  const getOptionLabel = useCallback(
    (data: SelectCustomizableOption, { context }: { context: string }): ReactNode =>
      useShortLabels && context === 'value' ? data.shortLabel : data.label,
    [useShortLabels]
  );

  const sharedStyles = useMemo<SharedStyles>(
    () => ({
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (provided) => ({
        ...provided,
        boxShadow: undefined,
        cursor: 'pointer',
        borderRadius: 'var(--select-customizable-border-radius)',
        transition: 'border-color 0.4s',
        ...getControlBorderStyles(!!error),
      }),
      option: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
        color: 'inherit',
      }),
    }),
    [error]
  );

  const selectStyles = useMemo<SelectStyles>(() => {
    if (!isSortedList) return sharedStyles;
    return {
      ...sharedStyles,
      control: (base, state = {}) => {
        const shared = sharedStyles.control(base, state);
        const { hasValue, isFocused } = state as { hasValue?: boolean; isFocused?: boolean };
        if (hasValue && !isFocused) {
          return {
            ...shared,
            border: '1px solid var(--select-customizable-border-default)',
            boxShadow: 'none',
          };
        }
        return shared;
      },
      valueContainer: (base) => ({
        ...base,
        padding: '2px 8px',
        overflow: 'hidden',
        cursor: 'pointer',
      }),
    };
  }, [isSortedList, sharedStyles]);

  const sharedProps = useMemo<SharedProps>(
    () => ({
      _listboxId: listboxId,
      _focusedOptionId: focusedOptionId,
      _onOptionFocusChange: onOptionFocusChange,
      _errorId: error ? errorId : undefined,
      _hasError: !!error,
      _required: !!required,
      _removeLabel: t('placeholder:Remove'),
      _valueId: valueId,
      onKeyDown: composedKeyDown,
      onMenuOpen: composedMenuOpen,
      onMenuClose: composedMenuClose,
      tabSelectsValue: false,
      // Props que consumen los componentes custom vía selectProps
      iconTypeIndicator,
      showValuesAsList: isSortedList,
      showValuesAsText,
      useShortLabels,
    }),
    [
      listboxId,
      focusedOptionId,
      onOptionFocusChange,
      error,
      errorId,
      required,
      t,
      valueId,
      composedKeyDown,
      composedMenuOpen,
      composedMenuClose,
      iconTypeIndicator,
      isSortedList,
      showValuesAsText,
      useShortLabels,
    ]
  );

  return {
    resolvedInputId,
    labelId,
    listboxId,
    errorId,
    selectRef,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    getCreateLabel,
    getScreenReaderStatus,
    sharedStyles,
    selectStyles,
    resolvedPlaceholder,
    resolvedAriaLabel,
    labelTitle,
    className,
    isSortedList,
    sharedProps,
    handleCreatableChange,
    formatGroupLabel,
    getOptionLabel,
  };
}
